import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  abTests,
  type AbTestResult,
  type AbTestInsights,
} from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  FlaskConical, Trophy, TrendingUp, Mail, BarChart3,
  ChevronDown, ChevronUp, ArrowRight, Sparkles, Target,
  Lightbulb, Calendar, Users, RefreshCw,
} from "lucide-react";

function fmtDate(d: string): string {
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function AbTestArchivePage() {
  const [tests, setTests] = useState<AbTestResult[]>([]);
  const [insights, setInsights] = useState<AbTestInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedTest, setExpandedTest] = useState<number | null>(null);

  useEffect(() => {
    abTests.list()
      .then(res => { setTests(res.tests); setInsights(res.insights); })
      .catch(e => console.error("A/B tests error:", e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="max-w-4xl mx-auto px-4 py-20 flex justify-center"><Spinner /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-ink flex items-center gap-2">
            <FlaskConical size={22} className="text-coral" /> A/B Test Archive
          </h1>
          <p className="text-slate text-sm mt-0.5">
            Historical results from every A/B test you've run. Learn what works for your audience.
          </p>
        </div>
      </div>

      {/* Insights card */}
      {insights && (
        <div className="bg-gradient-to-r from-coral-light to-white rounded-xl border border-coral/15 p-5 mb-6">
          <h2 className="font-display font-bold text-ink text-sm flex items-center gap-2 mb-3">
            <Lightbulb size={16} className="text-coral" /> What Your Data Says
          </h2>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded-lg p-3 text-center border border-sand/60">
              <p className="font-display font-bold text-ink text-xl">{insights.totalTests}</p>
              <p className="text-[10px] text-slate">Tests Run</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center border border-sand/60">
              <p className="font-display font-bold text-ink text-xl">{insights.avgWinnerOpenRate}%</p>
              <p className="text-[10px] text-slate">Avg Winner Open Rate</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center border border-sand/60">
              <p className="font-display font-bold text-coral text-xl">+{insights.avgUplift}%</p>
              <p className="text-[10px] text-slate">Avg Uplift vs Runner-Up</p>
            </div>
          </div>

          {insights.patterns.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-ink mb-2">Patterns for your audience:</h3>
              <div className="space-y-1.5">
                {insights.patterns.map((p, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-slate">
                    <Sparkles size={12} className="text-coral flex-shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {tests.length === 0 && (
        <div className="bg-white rounded-xl border border-sand py-16 text-center">
          <FlaskConical size={40} className="mx-auto text-slate/20 mb-3" />
          <h3 className="font-display font-bold text-ink text-lg">No A/B Tests Yet</h3>
          <p className="text-sm text-slate mt-1 max-w-sm mx-auto mb-5">
            Run your first A/B test by adding subject line variants when creating a campaign. Results will appear here automatically.
          </p>
          <Link to="/campaigns"
            className="inline-flex items-center gap-2 bg-coral hover:bg-coral-dark text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors">
            <Mail size={14} /> Create a Campaign with Variants
          </Link>
        </div>
      )}

      {/* Test list */}
      {tests.length > 0 && (
        <div className="space-y-3">
          {tests.map((test, ti) => {
            const isExpanded = expandedTest === ti;
            const winnerVariant = test.variants.find(v => v.label === test.winner.label);

            return (
              <div key={test.campaignId}
                className={`bg-white rounded-xl border transition-all ${isExpanded ? "border-coral/30 shadow-sm" : "border-sand hover:border-coral/20"}`}>

                {/* Test header */}
                <button onClick={() => setExpandedTest(isExpanded ? null : ti)}
                  className="w-full flex items-center gap-4 p-5 text-left">
                  {/* Winner badge */}
                  <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Trophy size={18} className="text-amber-500" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <h3 className="text-sm font-bold text-ink truncate">{test.campaignName}</h3>
                      <span className="text-[10px] text-slate flex items-center gap-1">
                        <Calendar size={10} /> {fmtDate(test.sentAt)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate">
                      <span className="flex items-center gap-1">
                        <FlaskConical size={11} /> {test.variantCount} variants
                      </span>
                      <span className="flex items-center gap-1">
                        <Users size={11} /> {test.totalSent.toLocaleString()} sent
                      </span>
                      <span className="flex items-center gap-1 text-emerald-600 font-semibold">
                        <Trophy size={11} /> {test.winner.label}: {test.winner.openRate}% opens
                        {test.winner.uplift > 0 && ` (+${test.winner.uplift}%)`}
                      </span>
                    </div>
                  </div>

                  {isExpanded ? <ChevronUp size={16} className="text-slate" /> : <ChevronDown size={16} className="text-slate" />}
                </button>

                {/* Expanded variant comparison */}
                {isExpanded && (
                  <div className="px-5 pb-5 pt-0">
                    {/* Winner callout */}
                    <div className="bg-amber-50 rounded-lg border border-amber-200 p-3 mb-4 flex items-center gap-3">
                      <Trophy size={16} className="text-amber-500 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-ink">Winner: {test.winner.label}</p>
                        <p className="text-xs text-slate mt-0.5">"{test.winner.subject}"</p>
                      </div>
                      {test.winner.uplift > 0 && (
                        <span className="ml-auto text-sm font-display font-bold text-emerald-600 flex items-center gap-1">
                          <TrendingUp size={14} /> +{test.winner.uplift}%
                        </span>
                      )}
                    </div>

                    {/* Variant comparison table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-sand">
                            <th className="text-left py-2 px-2 text-xs font-semibold text-slate">Variant</th>
                            <th className="text-left py-2 px-2 text-xs font-semibold text-slate">Subject Line</th>
                            <th className="text-right py-2 px-2 text-xs font-semibold text-slate">Sent</th>
                            <th className="text-right py-2 px-2 text-xs font-semibold text-slate">Opens</th>
                            <th className="text-right py-2 px-2 text-xs font-semibold text-slate">Open Rate</th>
                            <th className="text-right py-2 px-2 text-xs font-semibold text-slate">Clicks</th>
                            <th className="text-right py-2 px-2 text-xs font-semibold text-slate">Click Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {test.variants.map((v) => {
                            const isWinner = v.label === test.winner.label;
                            return (
                              <tr key={v.index} className={`border-b border-sand/50 ${isWinner ? "bg-amber-50/50" : ""}`}>
                                <td className="py-2.5 px-2">
                                  <div className="flex items-center gap-1.5">
                                    {isWinner && <Trophy size={11} className="text-amber-500" />}
                                    <span className={`text-xs font-semibold ${isWinner ? "text-amber-700" : "text-ink"}`}>
                                      {v.label}
                                    </span>
                                  </div>
                                </td>
                                <td className="py-2.5 px-2 text-xs text-slate max-w-[200px] truncate">{v.subject}</td>
                                <td className="py-2.5 px-2 text-xs text-slate text-right">{v.sent.toLocaleString()}</td>
                                <td className="py-2.5 px-2 text-xs text-slate text-right">{v.opens.toLocaleString()}</td>
                                <td className="py-2.5 px-2 text-right">
                                  <span className={`text-xs font-bold ${isWinner ? "text-emerald-600" : "text-ink"}`}>
                                    {v.openRate}%
                                  </span>
                                </td>
                                <td className="py-2.5 px-2 text-xs text-slate text-right">{v.clicks.toLocaleString()}</td>
                                <td className="py-2.5 px-2 text-xs text-ink text-right font-medium">{v.clickRate}%</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Visual bar comparison */}
                    <div className="mt-4">
                      <p className="text-[10px] font-semibold text-slate uppercase tracking-wide mb-2">Open Rate Comparison</p>
                      <div className="space-y-2">
                        {test.variants.map((v) => {
                          const maxRate = Math.max(...test.variants.map(x => x.openRate), 1);
                          const isWinner = v.label === test.winner.label;
                          return (
                            <div key={v.index} className="flex items-center gap-3">
                              <span className="text-[10px] text-slate w-16 text-right flex-shrink-0">{v.label}</span>
                              <div className="flex-1 h-5 bg-paper rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-500 ${isWinner ? "bg-coral" : "bg-slate/30"}`}
                                  style={{ width: `${(v.openRate / maxRate) * 100}%` }}
                                />
                              </div>
                              <span className={`text-xs font-bold w-12 text-right ${isWinner ? "text-coral" : "text-slate"}`}>
                                {v.openRate}%
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Bottom tip */}
      {tests.length > 0 && tests.length < 5 && (
        <div className="mt-6 bg-paper rounded-xl border border-sand p-5 text-center">
          <Target size={20} className="mx-auto text-coral mb-2" />
          <p className="text-sm text-ink font-semibold">Run more tests for better insights</p>
          <p className="text-xs text-slate mt-1 max-w-md mx-auto">
            The patterns panel appears after 2+ tests and gets more accurate with every test you run.
            Aim for at least 5 tests to see reliable trends for your audience.
          </p>
        </div>
      )}
    </div>
  );
}
